import { useCallback, useRef, useState } from "react";

import { postApi } from "../apis/api";
import { DOWNLOAD_IMPORT_TEMPLATE } from "../apis/end-points";
import { useAgentNumber } from "../context/AgentNumberContext";
import { useMessage } from "../context/MessageContext";
import { resolveAgentNumber as resolveAgentNumberById } from "../utils/agentNumber";
import {
  HUB_SCOPED_TEMPLATE_TYPES,
  ImportTemplateType,
} from "../utils/constants";
import { triggerBrowserDownload } from "../utils/fileDownload";
import { TEMPLATE_DOWNLOAD_MESSAGES } from "../utils/message";

/** `DOWNLOAD_IMPORT_TEMPLATE` response payload. */
interface DownloadTemplateResponse {
  url: string;
  fileName: string;
}

export interface DownloadTemplateParams {
  /** Template the API should generate. */
  templateType: ImportTemplateType;
  /** Agent whose data pre-fills the template. Resolved to an agent number when `agentNumber` is not supplied. */
  agentId?: string;
  /** Agent number, when the caller already has it — skips the resolve call. */
  agentNumber?: string;
  /** Agent hub id — required only for `HUB_SCOPED_TEMPLATE_TYPES`. */
  hubId?: string;
}

/** Hub scoped templates are the only ones allowed to send `hubId`. */
const isHubScoped = (templateType: ImportTemplateType) =>
  HUB_SCOPED_TEMPLATE_TYPES.includes(templateType);

/**
 * Downloads an agent scoped import template in two steps:
 *
 * 1. Resolve the agent number for the agent.
 * 2. Ask the import service for a pre-signed template URL and hand it to the browser.
 *
 * Kept separate from the button so any UI (button, menu item, modal action) can
 * trigger the same flow.
 */
const useDownloadTemplate = () => {
  const { setMessage } = useMessage();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  // Agent screens resolve the number once on load — `null` off those screens.
  const agentNumberState = useAgentNumber();
  // Guards against double submits without depending on the async state value.
  const inFlightRef = useRef<boolean>(false);

  /**
   * Cheapest source first: what the caller passed, then what the surrounding
   * agent screen already resolved, then the shared cache / API lookup.
   */
  const resolveAgentNumber = useCallback(
    ({ agentId, agentNumber }: DownloadTemplateParams): Promise<string> => {
      if (agentNumber) return Promise.resolve(agentNumber);
      if (!agentId) return Promise.resolve("");

      if (agentNumberState?.agentId === agentId && agentNumberState.agentNumber) {
        return Promise.resolve(agentNumberState.agentNumber);
      }

      return resolveAgentNumberById(agentId);
    },
    [agentNumberState]
  );

  const downloadTemplate = useCallback(
    async (params: DownloadTemplateParams) => {
      const { templateType, hubId } = params;
      if (inFlightRef.current) return;

      if (isHubScoped(templateType) && !hubId) {
        setMessage(TEMPLATE_DOWNLOAD_MESSAGES.HUB_REQUIRED, "error");
        return;
      }

      try {
        inFlightRef.current = true;
        setIsDownloading(true);

        const agentNumber = await resolveAgentNumber(params);
        if (!agentNumber) {
          setMessage(TEMPLATE_DOWNLOAD_MESSAGES.AGENT_NUMBER_NOT_FOUND, "error");
          return;
        }

        // Every template here is agent scoped, so the request is always tagged as such.
        const response = await postApi<DownloadTemplateResponse>(
          `${DOWNLOAD_IMPORT_TEMPLATE}?source=agent`,
          {
            agentNumber,
            templateType,
            ...(isHubScoped(templateType) && { hubId }),
          }
        );

        if (!response?.success || !response?.data?.url) {
          setMessage(
            response?.error?.message || TEMPLATE_DOWNLOAD_MESSAGES.DOWNLOAD_FAILED,
            "error"
          );
          return;
        }

        triggerBrowserDownload(
          response.data.url,
          response.data.fileName,
          `${templateType}.xlsx`
        );
        // Prefer the API's message — it describes what the service actually did.
        setMessage(response.message || TEMPLATE_DOWNLOAD_MESSAGES.SUCCESS, "success");
      } catch (error) {
        console.error("Template download failed:", error);
        setMessage(TEMPLATE_DOWNLOAD_MESSAGES.DOWNLOAD_FAILED, "error");
      } finally {
        inFlightRef.current = false;
        setIsDownloading(false);
      }
    },
    [resolveAgentNumber, setMessage]
  );

  return { downloadTemplate, isDownloading };
};

export default useDownloadTemplate;
